import React, { forwardRef, useMemo, useEffect } from "react";
import { MeshLineGeometry as MeshLine, MeshLineMaterial } from "meshline";
import { MathUtils } from "three";
import * as THREE from "three";
import { addTextLabel, makeCircleMarker } from "../3d/utils/threeUtils";
import {
  generateArc,
  createArcMesh,
  isValidVLSLayout,
} from "../3d/utils/layoutUtils";
import {
  computeFingerLayout,
  computeTwoHandedFingerLayout,
  isValidSpanMeasurements,
} from "../3d/utils/fingerLayoutUtils";
import { apiConfig } from "../api/api";
import { useSafeTexture } from "../../hooks/useSafeTexture";

const ballRadius = 4.294;
const arcSegments = 256;
const arcOffset = 0.005;
const arcDistancePinToMB = 6.75;

export const BowlingLayoutVLS = forwardRef((props, ref) => {
  const {
    pinToPapDistance,
    mbToPapDistance,
    pinBufferDistance,
    playerPapOffset,
    spanMeasurements,
    cameraPosition,
    ballId,
    access_token,
    onTextureStatusChange,
    onLayoutError,
  } = props;

  const handednessFactor = spanMeasurements?.isLeftHanded ? -1 : 1;
  const imageTextureUrl =
    ballId && access_token
      ? `${apiConfig.apiServer}ballDrilled/${ballId}/texture?access_token=${access_token}`
      : null;

  const { texture, isLoading } = useSafeTexture(
    imageTextureUrl,
    onTextureStatusChange,
  );

  const layoutIsValid = useMemo(
    () =>
      isValidVLSLayout({
        pinToPapDistance,
        pasToPapDistance: mbToPapDistance,
        pinBufferDistance,
      }),
    [pinToPapDistance, mbToPapDistance, pinBufferDistance],
  );

  // Verificar si playerPapOffset tiene valores numéricos válidos
  const hasValidPapOffset = useMemo(() => {
    const v = playerPapOffset?.vertical;
    const h = playerPapOffset?.horizontal;
    return (
      typeof v === "number" &&
      !isNaN(v) &&
      v > 0 &&
      typeof h === "number" &&
      !isNaN(h) &&
      h > 0
    );
  }, [playerPapOffset]);

  // Notificar error de layout al padre
  useEffect(() => {
    const hasError = !layoutIsValid || !hasValidPapOffset;
    onLayoutError?.(hasError);
  }, [layoutIsValid, hasValidPapOffset, onLayoutError]);

  const ballGeometry = new THREE.SphereGeometry(ballRadius, 256, 256);

  const ballMaterial = new THREE.MeshStandardMaterial({
    map: texture,
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0.2,
  });
  const ball = new THREE.Mesh(ballGeometry, ballMaterial);

  const makeMarker = (pos, color) => {
    const radius = 0.15;
    const geometry = new THREE.CircleGeometry(radius, 32);
    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
    });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(pos);
    marker.lookAt(pos.clone().add(pos.clone().normalize()));
    ball.add(marker);
  };

  const pin = new THREE.Vector3(0, ballRadius, 0);
  makeMarker(pin, 0xff0000);

  const axis = new THREE.Vector3(1, 0, 0);
  const thetaPinToMB = arcDistancePinToMB / ballRadius;

  const mb = pin
    .clone()
    .applyAxisAngle(axis, handednessFactor * thetaPinToMB)
    .normalize()
    .multiplyScalar(ballRadius);
  const cg = pin
    .clone()
    .applyAxisAngle(axis, (handednessFactor * thetaPinToMB) / 2)
    .normalize()
    .multiplyScalar(ballRadius);

  const cgNormal = cg.clone().normalize();
  addTextLabel({
    text: "CG",
    position: cg,
    normal: cgNormal,
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  const mbNormalText = mb.clone().normalize();
  addTextLabel({
    text: "PSA",
    position: mb,
    normal: mbNormalText,
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  // Si el layout no es válido o playerPapOffset no tiene valores válidos,
  // mostrar solo la bola básica con Pin, CG, PSA
  if (!layoutIsValid || !hasValidPapOffset) {
    return (
      <group ref={ref} position={[0, 0, 0]}>
        <primitive object={ball} />
      </group>
    );
  }

  // Verificar si tenemos specsheet válida para los fingers
  const hasValidSpecsheet = isValidSpanMeasurements(spanMeasurements);

  if (layoutIsValid) {
    const a = mbToPapDistance / ballRadius;
    const b = pinToPapDistance / ballRadius;
    const c = arcDistancePinToMB / ballRadius;

    const cosAngleAtPin =
      (Math.cos(a) - Math.cos(b) * Math.cos(c)) / (Math.sin(b) * Math.sin(c));
    const angleAtPinRad = Math.acos(MathUtils.clamp(cosAngleAtPin, -1, 1));

    const pinNormal = pin.clone().normalize();
    const baseAxis = new THREE.Vector3(1, 0, 0);
    const arcAxis = baseAxis
      .clone()
      .applyAxisAngle(pinNormal, handednessFactor * angleAtPinRad);

    const pap = pin
      .clone()
      .applyAxisAngle(
        arcAxis,
        (handednessFactor * pinToPapDistance) / ballRadius,
      )
      .normalize()
      .multiplyScalar(ballRadius);

    addTextLabel({
      text: "PAP",
      position: pap,
      normal: pap.clone().normalize(),
      ball,
      color: "#ffaa00",
      width: 2,
      lift: 0.05,
    });

    const arcPointsA = generateArc({
      center: pap,
      axis: pinNormal,
      radius: ballRadius,
      angleSweep: 2 / ballRadius,
      handednessFactor,
      arcOffset,
    });
    ball.add(createArcMesh(arcPointsA));

    const mbNormal = mb.clone().normalize();
    const toPAP = pap.clone().sub(mb).normalize();
    const projectedDirMB = toPAP
      .clone()
      .sub(mbNormal.clone().multiplyScalar(toPAP.dot(mbNormal)))
      .normalize();
    const mbBufferAngle = mbToPapDistance / ballRadius;
    const mbArcCenter = mb
      .clone()
      .applyAxisAngle(
        mbNormal.clone().cross(projectedDirMB).normalize(),
        mbBufferAngle,
      )
      .normalize()
      .multiplyScalar(ballRadius);

    const arcPoints3 = generateArc({
      center: mbArcCenter,
      axis: mbNormal,
      radius: ballRadius,
      angleSweep: 2 / ballRadius,
      handednessFactor,
      arcOffset,
    });
    ball.add(createArcMesh(arcPoints3, 0xffffff));

    const AB = pinToPapDistance / ballRadius;
    const AC = pinBufferDistance / ballRadius;
    const sinB = Math.asin(MathUtils.clamp(Math.sin(AC) / Math.sin(AB), -1, 1));
    const angleAtPAP = sinB;

    const valAxis = arcAxis
      .clone()
      .applyAxisAngle(pap.clone().normalize(), handednessFactor * -angleAtPAP)
      .normalize();

    const forwardLength = playerPapOffset.vertical;
    const backwardLength = pinToPapDistance <= 4 ? 5 : 6;
    const totalLength = forwardLength + backwardLength;
    const thetaTotal = totalLength / ballRadius;

    const generateArcFromPoint = ({
      startPoint,
      axis,
      arcLength,
      direction = 1,
      segments = arcSegments,
    }) => {
      const points = [];
      const angleSweep = arcLength / ballRadius;
      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const angle = direction * angleSweep * t;
        const p = startPoint
          .clone()
          .applyAxisAngle(axis, angle)
          .normalize()
          .multiplyScalar(ballRadius + arcOffset);
        points.push(p);
      }
      return points;
    };

    const arcPointsVAL = generateArcFromPoint({
      startPoint: pap,
      axis: valAxis,
      arcLength: forwardLength,
      direction: handednessFactor,
    });

    const arcPointsVALBack = generateArcFromPoint({
      startPoint: pap,
      axis: valAxis,
      arcLength: backwardLength,
      direction: -handednessFactor,
    });

    ball.add(createArcMesh(arcPointsVAL));
    ball.add(createArcMesh(arcPointsVALBack));

    const playerPAP = pap
      .clone()
      .applyAxisAngle(
        valAxis,
        (handednessFactor * playerPapOffset.vertical) / ballRadius,
      );
    const perpAxis = new THREE.Vector3()
      .crossVectors(valAxis, playerPAP.clone().normalize())
      .normalize()
      .multiplyScalar(handednessFactor);

    const markerDistance = playerPapOffset.horizontal;
    const totalArcLength = markerDistance + 2;
    const thetaArc2 = totalArcLength / ballRadius;
    const markerRatio = markerDistance / totalArcLength;

    const arcPoints4 = [];
    let markerPoint = null;

    for (let i = 0; i <= arcSegments; i++) {
      const t = i / arcSegments;
      const angle = thetaArc2 * t;
      const point = playerPAP
        .clone()
        .applyAxisAngle(perpAxis, handednessFactor * angle)
        .normalize()
        .multiplyScalar(ballRadius + arcOffset);
      if (markerPoint === null && t >= markerRatio) {
        markerPoint = point.clone();
      }
      arcPoints4.push(point);
    }

    const VALHorizontalLine = new MeshLine();
    VALHorizontalLine.setPoints(arcPoints4);
    const VALHorizontal = new THREE.Mesh(
      VALHorizontalLine,
      new MeshLineMaterial({ color: 0xffffff, lineWidth: 0.05 }),
    );
    ball.add(VALHorizontal);

    let gripLineDir = null;
    if (markerPoint) {
      const thetaArc = 3 / ballRadius;
      const arcCenterDir = playerPAP.clone().normalize();
      let basePerpAxis = new THREE.Vector3()
        .crossVectors(arcCenterDir, markerPoint.clone().normalize())
        .normalize();
      if (perpAxis.lengthSq() > 1e-8 && basePerpAxis.dot(perpAxis) < 0) {
        basePerpAxis = basePerpAxis.negate();
      }
      const rotatedAxis = basePerpAxis
        .clone()
        .applyAxisAngle(markerPoint.clone().normalize(), Math.PI / 2);
      const finalPerp = rotatedAxis.normalize();

      gripLineDir = new THREE.Vector3()
        .crossVectors(finalPerp, markerPoint.clone().normalize())
        .normalize();

      const arcPoints5 = [];
      for (let i = 0; i <= arcSegments; i++) {
        const t = i / arcSegments;
        const angle = handednessFactor * thetaArc * (t - 0.5);
        const point = markerPoint
          .clone()
          .applyAxisAngle(finalPerp, angle)
          .normalize()
          .multiplyScalar(ballRadius + arcOffset);
        arcPoints5.push(point);
      }

      const gripCenterLine = new MeshLine();
      gripCenterLine.setPoints(arcPoints5);
      const gripCenter = new THREE.Mesh(
        gripCenterLine,
        new MeshLineMaterial({ color: 0xffffff, lineWidth: 0.05 }),
      );
      ball.add(gripCenter);
    }

    // === Compute pin buffer arc ===
    if (markerPoint) {
      // Step 1: Resample backward along VAL arc to find tangent point
      const arcSamples = 1024;
      const maxSweepAngle = 10 / ballRadius;
      let bestMatch = Infinity;
      let tangentPoint = null;

      for (let i = 0; i <= arcSamples; i++) {
        const t = i / arcSamples;
        const sweep = handednessFactor * -t * maxSweepAngle;
        const point = pap
          .clone()
          .applyAxisAngle(valAxis, sweep)
          .normalize()
          .multiplyScalar(ballRadius);

        const distToPin = point.angleTo(pin) * ballRadius;
        const delta = Math.abs(distToPin - pinBufferDistance);
        if (delta < bestMatch) {
          bestMatch = delta;
          tangentPoint = point.clone();
        }
      }

      if (tangentPoint) {
        const pinNormal = pin.clone().normalize();
        const toTangent = tangentPoint.clone().sub(pin).normalize();

        const projectedDir = toTangent
          .clone()
          .sub(pinNormal.clone().multiplyScalar(toTangent.dot(pinNormal)))
          .normalize();

        const rotationAxis = new THREE.Vector3()
          .crossVectors(pinNormal, projectedDir)
          .normalize();

        const pinBufferAngle = pinBufferDistance / ballRadius;
        const bufferCenterPoint = pin
          .clone()
          .applyAxisAngle(rotationAxis, pinBufferAngle)
          .normalize()
          .multiplyScalar(ballRadius);

        const localRingRadius = ballRadius * Math.sin(pinBufferAngle);
        const arcLength = 2;
        const angleSweep = arcLength / localRingRadius;

        const arcPointsBuffer = generateArc({
          center: bufferCenterPoint,
          axis: pinNormal,
          radius: ballRadius,
          angleSweep,
          handednessFactor,
          arcOffset,
        });

        ball.add(createArcMesh(arcPointsBuffer, 0xffffff));
      }
    }

    if (markerPoint && hasValidSpecsheet) {
      let thumbPointLocal = null;
      if (spanMeasurements?.is2Hander) {
        computeTwoHandedFingerLayout({
          ball,
          centerPoint: markerPoint,
          targetPoint: playerPAP,
          spanMeasurements,
          ballRadius,
          arcOffset,
        });
      } else {
        const { thumb } = computeFingerLayout({
          ball,
          centerPoint: markerPoint,
          spanMeasurements,
          ballRadius,
          arcOffset,
          verticalAxis: valAxis,
          referenceAxis: perpAxis,
        });
        thumbPointLocal = thumb.clone();
      }

      // Paso 1: dirección inicial que apunta desde centro hacia centerGrip
      const markerPointLocal = markerPoint.clone();
      const markerPointWorld = markerPointLocal
        .clone()
        .applyMatrix4(ball.matrixWorld);

      const cameraTargetPosition = new THREE.Vector3().fromArray(
        cameraPosition,
      );
      const fromCenterToMarker = markerPointWorld
        .clone()
        .sub(ball.position)
        .normalize();
      const fromCenterToCamera = cameraTargetPosition
        .clone()
        .sub(ball.position)
        .normalize();

      // Paso 2: alinear marker → cámara (ya funciona bien en tu código)
      const rotationQuat = new THREE.Quaternion().setFromUnitVectors(
        fromCenterToMarker,
        fromCenterToCamera,
      );
      ball.quaternion.premultiply(rotationQuat);

      // Paso 3: aplicar la misma rotación a thumb para saber dónde quedó
      const camForward = fromCenterToCamera.clone();
      let projectedUp = null;
      if (gripLineDir && gripLineDir.lengthSq() > 1e-8) {
        const rotatedGripLine = gripLineDir
          .clone()
          .applyQuaternion(ball.quaternion);
        projectedUp = rotatedGripLine
          .clone()
          .projectOnPlane(camForward)
          .normalize();
      } else if (thumbPointLocal) {
        const rotatedThumb = thumbPointLocal
          .clone()
          .applyQuaternion(ball.quaternion)
          .add(ball.position);
        // Paso 4: proyectar vector de 'up' desde marker hacia thumb en plano perpendicular a la cámara
        projectedUp = rotatedThumb
          .clone()
          .sub(markerPointWorld)
          .projectOnPlane(camForward)
          .normalize();
      }

      // Paso 5: calcular ángulo entre el vector `projectedUp` y el vector que debería ser "up" (eje Y pantalla)
      if (projectedUp && projectedUp.lengthSq() > 1e-8) {
        const screenDown = new THREE.Vector3(0, -1, 0); // dirección vertical en pantalla
        const angle = projectedUp.angleTo(screenDown);
        const cross = new THREE.Vector3().crossVectors(projectedUp, screenDown);
        const sign = Math.sign(cross.dot(camForward)); // determina sentido

        // Paso 6: rotar alrededor del eje de cámara para corregir el roll
        const rollQuat = new THREE.Quaternion().setFromAxisAngle(
          camForward,
          angle * sign,
        );
        ball.quaternion.premultiply(rollQuat);
      }
    }
  }

  return (
    <group ref={ref}>
      <primitive {...{ object: ball }} />
    </group>
  );
});

BowlingLayoutVLS.displayName = "BowlingLayoutVLS";
